// All effects consume acknowledged events/snapshots; nothing here decides a collision.
import { WEAPON_FX } from './fx-flags';
import type { Graphics } from 'pixi.js';
import type { ProjSnap } from '../../common/src/protocol';
import { CITY_ART, RARITIES } from '../../common/src/data';
import { COLOR } from './theme';
import { weaponKind } from './character-motion';
import { FxPool, flashStrength } from './fx-pool';

export const WF = CITY_ART.weaponFx;
export const WEAPON_FX_REVIEW = WEAPON_FX;
export const comfortScale = (grade: number, low = false) => WF.comfort[low ? 2 : grade] ?? WF.comfort[2];
type Spark = { x: number; y: number; vx: number; vy: number; at: number; body: boolean; big: boolean };
type Casing = { x: number; y: number; a: number; at: number; shell: boolean; seed: number };
type Shot = { x: number; y: number; a: number; at: number; color: string; weapon: string; shown: boolean };
type Trail = { id: number; x: number; y: number; vx: number; vy: number; at: number; color: string; points: number[]; matched: boolean };
type Mark = { x: number; y: number; a: number; at: number };

const FLASH_GAIN = 1.8;
const MUZZLE_SHAPES: Record<string, number[]> = {
    pistol: [0,-1, 3,-2, 5,0, 3,2, 0,1],
    smg: [0,-1.5, 3,-3, 5,-1.5, 8,0, 5,1.5, 3,3, 0,1.5],
    rifle: [0,-1.5, 4,-4.5, 5,-1.5, 12,0, 5,1.5, 4,4.5, 0,1.5],
    shotgun: [0,-1, 3,-4, 6,-3, 9,0, 6,3, 3,4, 0,1],
    smart: [0,-1, 3,-2, 6,-1, 4,0, 6,1, 3,2, 0,1],
    coilgun: [0,-1, 9,-1, 11,0, 9,1, 0,1],
};
/** Family silhouettes at the held sprite's real bore, solid and bright (never weaker than the old amber flash). No glow filter or camera kick. */
export function muzzleFlash(g: Graphics, weapon: string, scale: number, x = 0, y = 0, angle = 0) {
  const family = weapon === 'smart' ? 'smart' : weaponKind(weapon);
  const shape = MUZZLE_SHAPES[family] ?? MUZZLE_SHAPES.pistol;
  const size = FLASH_GAIN * (.6 + .4 * scale), c = Math.cos(angle), s = Math.sin(angle);
  const rot = (shape2: number[], k: number) => { const out: number[] = []; for (let i = 0; i < shape2.length; i += 2) out.push(Math.round(x + (c * shape2[i] - s * shape2[i + 1]) * k), Math.round(y + (s * shape2[i] + c * shape2[i + 1]) * k)); return out; };
  const cool = family === 'smart' || family === 'coilgun';
  g.poly(rot(shape, size)).fill({ color: cool ? COLOR.interactive : COLOR.world });
  g.poly(rot(shape, size * .55)).fill({ color: COLOR.text });
  if (cool) g.poly(rot(shape, size * 1.25)).stroke({ color: COLOR.interactive, width: 1, alpha: .8 });
}
export class WeaponFx {
  readonly sparks = new FxPool<Spark>(WF.particles);
  readonly casings = new FxPool<Casing>(WF.casings);
  readonly marks = new FxPool<Mark>(WF.decals);
  readonly shots = new FxPool<Shot>(WF.shots);
  readonly trails = new FxPool<Trail>(WF.trails);
  readonly hits = new FxPool<{ key: string; x: number; y: number; at: number; big: boolean; shown: boolean }>(WF.decals);
  private serial = 0;
  shot(x: number, y: number, a: number, weapon: string, rarity: string, t: number, groundX = x, groundY = y) {
    const color = rarity === 'common' ? COLOR.text : RARITIES.tiers[rarity as keyof typeof RARITIES.tiers]?.color ?? COLOR.text;
    this.shots.put({ x, y, a, weapon, color, at: t, shown: false });
    if (weapon !== 'charge') this.casings.put({ x: groundX, y: groundY + 4, a, at: t + (weapon === 'vs12' ? WF.shotgunEjectSec : 0), shell: weapon === 'vs12', seed: this.serial++ });
  }
  damage(key: string, amount: number, x: number, y: number, a: number, melee: boolean, t: number) {
    this.hits.put({ key, x, y, at: t, big: amount >= WF.highDamage, shown: false });
    if (melee) this.marks.put({ x, y, a, at: t });
  }
  hit(key: string, t: number) { return this.hits.items.find(h => h.key === key && flashStrength(h, t, WF.flashSec, WF.eventGraceSec) > 0); }
  impact(x: number, y: number, body: boolean, t: number) {
    // Direction is cosmetic inference from the recent observed projectile, never a hit test.
    let closest: Trail | undefined, distance = 48;
    for (const p of this.trails.items) {
      if (t - p.at > .25) continue;
      const dt = Math.max(0, Math.min(.1, t - p.at));
      const d = Math.hypot(p.x + p.vx * dt - x, p.y + p.vy * dt - y);
      if (d < distance) { distance = d; closest = p; }
    }
    const a = closest ? Math.atan2(closest.vy, closest.vx) : 0;
    const big = this.hits.items.some(h => t - h.at >= 0 && t - h.at < .05 && h.big && Math.hypot(h.x - x, h.y - y) < 18);
    for (let i = 0; i < (big ? 6 : 3); i++) {
      const angle = a + (i - 1) * .45, speed = body ? 22 + i * 5 : 35 + i * 7;
      this.sparks.put({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, body, big, at: t });
    }
  }
  projectile(b: ProjSnap, g: Graphics, t: number) {
    let p = this.trails.items.find(p => p.id === b.id);
    if (!p) p = this.trails.put({ ...b, at: t, color: b.k === 4 ? COLOR.danger : COLOR.text, points: [], matched: b.k === 4 });
    if (!p.matched) {
      // The protocol has no projectile owner. Associate only when an observed shot is close to its back-projected origin.
      let shot: Shot | undefined, best = 40, second = Infinity;
      for (const s of this.shots.items) {
        const age = t - s.at; if (age < 0 || age > .25) continue;
        const d = Math.hypot(b.x - b.vx * Math.min(.1, age) - s.x, b.y - b.vy * Math.min(.1, age) - s.y);
        if (d < best) { second = best; best = d; shot = s; } else second = Math.min(second, d);
      }
      if (shot && second - best > 4) { p.color = shot.color; p.matched = true; }
    }
    if (Math.hypot(b.x - p.x, b.y - p.y) > 1) {
      p.points.push(Math.round(p.x), Math.round(p.y)); if (p.points.length > 8) p.points.splice(0, 2);
    }
    Object.assign(p, b, { at: t });
    const x = Math.round(b.x), y = Math.round(b.y), seconds = b.k === 6 ? .014 : b.k === 2 ? .035 : .026;
    if (b.k === 3 && p.points.length >= 2) {
      g.moveTo(p.points[0], p.points[1]); for (let i = 2; i < p.points.length; i += 2) g.lineTo(p.points[i], p.points[i + 1]);
      g.lineTo(x, y).stroke({ color: p.color, width: 1, alpha: .7 });
    }
    g.moveTo(x, y).lineTo(Math.round(b.x - b.vx * seconds), Math.round(b.y - b.vy * seconds))
      .stroke({ color: p.color, width: b.k === 2 || b.k === 5 ? 2 : 1, alpha: b.k === 6 ? .8 : 1 });
    g.moveTo(x, y).lineTo(Math.round(b.x - b.vx * seconds * .35), Math.round(b.y - b.vy * seconds * .35)).stroke({ color: COLOR.text, width: 1, alpha: .9 });
  }
  draw(g: Graphics, ground: Graphics, t: number, scale: number) {
    ground.clear();
    for (const c of this.casings.items) {
      const age = t - c.at; if (age < 0 || age > WF.casingSec) continue;
      // One analytic bounce: two short parabolas, then a stationary ground pixel.
      const u = Math.min(age, .4), hop = age < .25 ? 8 * (1 - (age / .25) ** 2) : age < .4 ? 4 * ((age - .25) / .15) * (1 - (age - .25) / .15) * 2 : 0;
      const side = c.seed % 2 ? 1 : -1;
      const x = Math.round(c.x - Math.sin(c.a) * side * u * 24), y = Math.round(c.y + Math.cos(c.a) * side * u * 16 - hop);
      ground.rect(x, y, c.shell ? 3 : 2, 2).fill({ color: COLOR.world, alpha: Math.min(1, (WF.casingSec - age) / .6) }).rect(x, y, 1, 1).fill({ color: COLOR.text, alpha: .9 });
    }
    for (const p of this.sparks.items) {
      const age = t - p.at; if (age < 0 || age > WF.sparkSec) continue;
      const x = Math.round(p.x + p.vx * age), y = Math.round(p.y + p.vy * age), fade = (1 - age / WF.sparkSec) * scale;
      if (p.body) g.rect(x, y, p.big ? 2 : 1, 1).fill({ color: COLOR.text, alpha: .7 * fade });
      else g.moveTo(x, y).lineTo(Math.round(x - p.vx * .025), Math.round(y - p.vy * .025)).stroke({ color: COLOR.world, width: 1, alpha: .8 * fade });
    }
    for (const m of this.marks.items) {
      const age = t - m.at; if (age < 0 || age > WF.decalSec) continue;
      const x = Math.round(m.x), y = Math.round(m.y), dx = Math.round(Math.cos(m.a + .8) * 4), dy = Math.round(Math.sin(m.a + .8) * 4);
      g.moveTo(x - dx, y - dy).lineTo(x + dx, y + dy).stroke({ color: COLOR.text, width: 1, alpha: (1 - age / WF.decalSec) * scale });
    }
  }
}
